import React, { useState, useCallback } from 'react';
import Map from './components/Map';
import FilterPanel from './components/FilterPanel';
import { FilterState } from './types';
import { searchAddress } from './utils/geocoding';
import './index.css';

function App() {
  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    showElementary: true,
    showMiddle: true,
    showHigh: true,
    searchTerm: ''
  });

  // Panel visibility state for mobile
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Address search state
  const [isSearching, setIsSearching] = useState(false);
  const [searchMarker, setSearchMarker] = useState<{lat: number, lng: number, address: string} | null>(null);

  // School count (will be updated when data loads)
  const [schoolCount, setSchoolCount] = useState({
    elementary: 0,
    middle: 0,
    high: 0,
    total: 0
  });

  // Assignment state (from map clicks)
  const [currentAssignment, setCurrentAssignment] = useState<{
    lat: number;
    lng: number;
    elementary?: string;
    middle?: string;
    high?: string;
  } | null>(null);

  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
  }, []);

  // Handle address search
  const handleAddressSearch = useCallback(async (address: string) => {
    setIsSearching(true);
    try {
      const response = await searchAddress(address);
      
      if (response.results.length === 0) {
        alert('No results found for that address in Frisco, Texas. Please try a different address.');
        return;
      }

      // Use the first (most relevant) result
      const result = response.results[0];
      
      // Set search marker to show on map
      setSearchMarker({
        lat: result.lat,
        lng: result.lng,
        address: result.display_name
      });

    } catch (error) {
      console.error('Address search error:', error);
      alert(error instanceof Error ? error.message : 'Unable to search for address. Please try again.');
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle location click (from map)
  const handleLocationClick = useCallback((lat: number, lng: number) => {
    // This is called when user clicks on the map
    // The Map component already handles showing the school assignment popup
    console.log('Location clicked:', lat, lng);
  }, []);

  // Handle school count update (called from Map component when data loads)
  const handleSchoolCountUpdate = useCallback((counts: typeof schoolCount) => {
    setSchoolCount(counts);
  }, []);

  // Handle assignment update (called from Map component when user clicks)
  const handleAssignmentUpdate = useCallback((assignment: typeof currentAssignment) => {
    setCurrentAssignment(assignment);
  }, []);

  // Handle clearing assignment (called from FilterPanel close button)
  const handleClearAssignment = useCallback(() => {
    setCurrentAssignment(null);
    // Also clear the search marker and focused assignment by calling the global function
    (window as any).clearFocusedView?.();
  }, []);

  return (
    <div className="flex h-screen relative">
      {/* Mobile Menu Button - Only show when panel is closed */}
      {!isPanelOpen && (
        <button
          onClick={() => setIsPanelOpen(!isPanelOpen)}
          className="md:hidden absolute top-4 left-4 z-[9999] bg-white shadow-lg rounded-lg p-3 border border-gray-200 hover:bg-gray-50 transition-colors"
          aria-label="Toggle menu"
          style={{ position: 'fixed' }}
        >
          <svg
            className="w-6 h-6 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Backdrop for mobile overlay */}
      {isPanelOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsPanelOpen(false)}
        />
      )}

      {/* Filter Panel - Desktop: sidebar, Mobile: overlay */}
      <div 
        className={`
          fixed md:relative
          top-0 left-0
          w-80 md:w-80
          h-full
          z-[9998] md:z-auto
          transition-transform duration-300 ease-in-out
          md:transition-none
          transform
          ${isPanelOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `}

      >
        <FilterPanel
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onAddressSearch={handleAddressSearch}
          isSearching={isSearching}
          schoolCount={schoolCount}
          currentAssignment={currentAssignment}
          onClearAssignment={handleClearAssignment}
          onMobileClose={() => setIsPanelOpen(false)}
        />
      </div>
      
      {/* Map - Full width on mobile, flex-1 on desktop */}
      <div className="flex-1 md:flex-1 w-full">
        <Map
          onLocationClick={handleLocationClick}
          filters={filters}
          searchMarker={searchMarker}
          onSchoolCountUpdate={handleSchoolCountUpdate}
          onClearSearchMarker={() => setSearchMarker(null)}
          onAssignmentUpdate={handleAssignmentUpdate}
        />
      </div>
    </div>
  );
}

export default App; 